import { Capacitor } from '@capacitor/core';

import { CustomHttpHeader } from 'record-it-time-common/enums/CustomHttpHeader';
import { ClientType } from 'record-it-time-common/enums/ClientType';
import { HttpMethod } from 'record-it-time-common/enums/HttpMethod';
import { AppResponse } from 'record-it-time-common/AppResponse';
import { PathUtils } from 'record-it-utils/utils/PathUtils/PathUtils';
import { EventDispatcher } from 'record-it-utils/events/EventDispatcher/EventDispatcher';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';

import { config } from '../../config';

/**
 * Do not directly use this class for api request. Use the ApiAdapter so the requests can be easily mocked
 */
export class ApiRequester {
  private static csrfToken: string | null = null;

  private static eventDispatcher = new EventDispatcher<{
    onAuthenticationError: void;
  }>();

  private constructor() {}

  public static onAuthenticationError(callback: () => void): Disposable {
    return this.eventDispatcher.addEventListener(
      'onAuthenticationError',
      callback
    );
  }

  public static setCsrfToken(csrfToken: string | null): void {
    this.csrfToken = csrfToken;
  }

  public static postJson<T extends AppResponse<unknown>>(
    url: string,
    options?: FetchOptionsWithoutMethod
  ): Promise<T> {
    return this.fetchJson(url, {
      ...options,
      method: HttpMethod.Post
    });
  }

  public static getJson<T extends AppResponse<unknown>>(
    url: string,
    options?: FetchOptionsWithoutMethod
  ): Promise<T> {
    return this.fetchJson(url, {
      ...options,
      method: HttpMethod.Get
    });
  }

  public static postFile(
    url: string,
    options?: FetchOptionsWithoutMethod
  ): Promise<Blob> {
    return this.fetchFile(url, {
      ...options,
      method: HttpMethod.Post
    });
  }

  public static async fetchJson<T extends AppResponse<unknown>>(
    url: string,
    options: FetchOptions
  ): Promise<T> {
    const response = await this.fetch(url, options);

    const jsonResponse = (await response.json()) as T;
    if (!jsonResponse.success && jsonResponse.error === 'ERR_INVALID_TOKEN') {
      this.eventDispatcher.dispatchEvent('onAuthenticationError', undefined);
    }

    return jsonResponse;
  }

  public static fetchFile(url: string, options: FetchOptions): Promise<Blob> {
    return this.fetch(url, options).then((r) => r.blob());
  }

  private static fetch(url: string, options: FetchOptions): Promise<Response> {
    const requestInit: CustomRequestInit = {
      method: options.method,
      credentials: 'include',
      headers: {
        [CustomHttpHeader.ClientType]: Capacitor.isNativePlatform()
          ? ClientType.App
          : ClientType.Web
      }
    };

    if (options.body) {
      requestInit.body = JSON.stringify(options.body);
      requestInit.headers['content-type'] = 'application/json';
    }

    if (this.csrfToken) {
      requestInit.headers[CustomHttpHeader.CsrfToken] = this.csrfToken;
    }

    return fetch(this.prefixUrl(url), requestInit);
  }

  private static prefixUrl(url: string): string {
    return PathUtils.joinPaths(config.get('ApiBaseUrl'), url);
  }
}

type CustomRequestInit = Omit<RequestInit, 'headers'> & {
  headers: Record<string, string>;
};

export type FetchOptions = {
  method: HttpMethod;
  body?: unknown;
};

export type FetchOptionsWithoutMethod = Omit<FetchOptions, 'method'>;
